package sarka.design.skoria.messaging

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*
import sarka.design.skoria.network.TokenStore
import sarka.design.skoria.network.createPlatformHttpClient
import sarka.design.skoria.network.defaultApiUrl

/**
 * HTTP side of direct messaging.
 *
 * Sending goes over HTTP rather than the socket so the server can commit the
 * message before notifying anyone. A dropped socket then delays delivery instead
 * of losing the message.
 */
class MessageApi(
    private val baseUrl: String = defaultApiUrl(),
    private val httpClient: HttpClient = createPlatformHttpClient(),
) {

    suspend fun conversations(): Result<List<ConversationSummary>> = call {
        httpClient.get("$baseUrl/messages") { auth() }.body()
    }

    suspend fun conversation(
        withUser: String,
        limit: Int = 50,
        before: Long? = null,
    ): Result<ConversationResponse> = call {
        httpClient.get("$baseUrl/messages/with/$withUser") {
            auth()
            parameter("limit", limit)
            if (before != null) parameter("before", before)
        }.body()
    }

    suspend fun send(
        recipient: String,
        payload: String,
        payloadVersion: Int,
    ): Result<SendMessageResponse> = call {
        val response = httpClient.post("$baseUrl/messages") {
            auth()
            contentType(ContentType.Application.Json)
            setBody(SendMessageRequest(recipient, payload, payloadVersion))
        }

        when (response.status) {
            HttpStatusCode.Created -> response.body()
            HttpStatusCode.NotFound -> throw MessageError.UnknownRecipient(recipient)
            HttpStatusCode.PayloadTooLarge -> throw MessageError.TooLong
            else -> throw MessageError.Server(response.status.value)
        }
    }

    suspend fun markRead(withUser: String): Result<Int> = call {
        val response = httpClient.post("$baseUrl/messages/read") {
            auth()
            contentType(ContentType.Application.Json)
            setBody(MarkReadRequest(withUser))
        }
        response.body<Map<String, Int>>()["markedRead"] ?: 0
    }

    suspend fun totalUnread(): Result<Int> = call {
        httpClient.get("$baseUrl/messages/unread") { auth() }
            .body<Map<String, Int>>()["unread"] ?: 0
    }

    /**
     * These routes sit behind `auth-jwt`, so every call needs the bearer token.
     * Unlike the WebSocket, an HTTP request can use a real header.
     */
    private fun HttpRequestBuilder.auth() {
        TokenStore.token.value?.let { header(HttpHeaders.Authorization, "Bearer $it") }
    }

    private inline fun <T> call(block: () -> T): Result<T> = try {
        Result.success(block())
    } catch (e: Throwable) {
        Result.failure(e.asMessageError())
    }
}

/** Failures worth distinguishing in the UI. */
sealed class MessageError(message: String) : Exception(message) {
    class UnknownRecipient(val username: String) : MessageError("No user called $username")
    object TooLong : MessageError("Message is too long")
    object NotAuthenticated : MessageError("Session expired. Please log in again.")
    object Offline : MessageError("Could not reach the server")
    class Server(val status: Int) : MessageError("Server error ($status)")
}

private fun Throwable.asMessageError(): Throwable = when {
    this is MessageError -> this
    message?.contains("401") == true -> MessageError.NotAuthenticated
    message?.contains("fetch", ignoreCase = true) == true -> MessageError.Offline
    message?.contains("Connection refused", ignoreCase = true) == true -> MessageError.Offline
    else -> this
}
